import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Users, GraduationCap, Key, Trash2, Search, ChevronDown, ChevronRight, School, UserPlus, FolderPlus, Upload, Download, FileSpreadsheet, Edit, Check, X, CheckSquare, Square, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement() {
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [expandedClasses, setExpandedClasses] = useState({});
  const [selectedClassForStudent, setSelectedClassForStudent] = useState(null);
  
  // Bulk import states
  const [bulkImportType, setBulkImportType] = useState('students');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState({});
  const studentsPerPage = 20;
  
  // Delete confirmation states
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deleteClassDialogOpen, setDeleteClassDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [classToDelete, setClassToDelete] = useState(null);
  
  // Assign classes to teacher states
  const [assignClassDialogOpen, setAssignClassDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  
  // Edit class name states
  const [editingClassId, setEditingClassId] = useState(null);
  const [editingClassName, setEditingClassName] = useState('');
  
  // Multi-select students states
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(''); // 'delete' or 'move'
  const [targetClassId, setTargetClassId] = useState('');
  
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'student',
    class_id: '',
  });
  
  const [classFormData, setClassFormData] = useState({
    name: '',
    grade: '',
  });
  
  const { getAuthHeaders, user: currentUser } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchClasses(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/admin/classes-list`, {
        headers: getAuthHeaders(),
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Сыныптарды жүктеу кезінде қате орын алды');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Пайдаланушыларды жүктеу кезінде қате орын алды');
    }
  };

  // Download template for bulk import
  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/bulk-upload/template/${type}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Үлгі файл жүктелді');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Үлгі файлды жүктеу кезінде қате орын алды');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Тек Excel файлдары қабылданады (.xlsx, .xls)');
        return;
      }
      setSelectedFile(file);
      setImportResults(null); // Reset previous results
    }
  };

  // Bulk import submit handler
  const handleBulkImportSubmit = async () => {
    if (!selectedFile) {
      toast.error('Файл таңдаңыз');
      return;
    }

    setImporting(true);
    setImportResults(null);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${API}/admin/bulk-upload/${bulkImportType}`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(response.data.message);
      setImportResults(response.data);
      
      // Refresh data
      await fetchClasses();
      await fetchUsers();
      
      // Reset file input
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error(error.response?.data?.detail || 'Импорт кезінде қате орын алды');
    } finally {
      setImporting(false);
    }
  };

  // Reset bulk import dialog
  const resetBulkImportDialog = () => {
    setBulkImportDialogOpen(false);
    setSelectedFile(null);
    setImportResults(null);
    setBulkImportType('students');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get import type label
  const getImportTypeLabel = (type) => {
    switch(type) {
      case 'students': return 'Оқушылар';
      case 'teachers': return 'Мұғалімдер';
      case 'classes': return 'Сыныптар';
      default: return type;
    }
  };

  // Get import type columns info
  const getImportTypeColumns = (type) => {
    switch(type) {
      case 'students':
        return [
          { name: 'full_name / аты-жөні', required: true, desc: 'Толық аты' },
          { name: 'class_name / сынып', required: false, desc: 'Сынып атауы' },
          { name: 'username / логин', required: false, desc: 'Логин (автоматты)' },
          { name: 'password / құпия сөз', required: false, desc: 'Әдепкі: student123' },
        ];
      case 'teachers':
        return [
          { name: 'full_name / аты-жөні', required: true, desc: 'Толық аты' },
          { name: 'username / логин', required: false, desc: 'Логин (автоматты)' },
          { name: 'password / құпия сөз', required: false, desc: 'Әдепкі: teacher123' },
        ];
      case 'classes':
        return [
          { name: 'name / сынып', required: true, desc: 'Сынып атауы' },
          { name: 'grade / сынып деңгейі', required: false, desc: 'Сынып деңгейі' },
        ];
      default:
        return [];
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/users`,
        {
          username: userFormData.username,
          password: userFormData.password,
          full_name: userFormData.full_name,
          role: userFormData.role,
          class_id: userFormData.role === 'student' ? userFormData.class_id : null,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`✅ ${userFormData.full_name} сәтті қосылды!`);
      setUserDialogOpen(false);
      setUserFormData({
        username: '',
        password: '',
        full_name: '',
        role: 'student',
        class_id: selectedClassForStudent || '',
      });
      setSelectedClassForStudent(null);
      fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      const errorMsg = error.response?.data?.detail || 'Пайдаланушыны қосу кезінде қате орын алды';
      toast.error(`❌ ${errorMsg}`);
    }
  };

  const handleClassSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/classes`,
        classFormData,
        { headers: getAuthHeaders() }
      );
      toast.success('Сынып қосылды');
      setClassDialogOpen(false);
      setClassFormData({ name: '', grade: '' });
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
      console.error('Error creating class:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 4) {
      toast.error('Құпия сөз кемінде 4 таңбадан тұруы керек');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success('Құпия сөз өзгертілді');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Қате орын алды');
      console.error('Error changing password:', error);
    }
  };

  // Open delete user confirmation dialog
  const openDeleteUserDialog = (user) => {
    setUserToDelete(user);
    setDeleteUserDialogOpen(true);
  };

  // Confirm delete user
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Пайдаланушы өшірілді');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
      console.error('Error deleting user:', error);
    } finally {
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
    }
  };

  // Open delete class confirmation dialog
  const openDeleteClassDialog = (cls) => {
    setClassToDelete(cls);
    setDeleteClassDialogOpen(true);
  };

  // Confirm delete class
  const confirmDeleteClass = async () => {
    if (!classToDelete) return;
    
    try {
      await axios.delete(`${API}/admin/classes/${classToDelete.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Сынып өшірілді');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
      console.error('Error deleting class:', error);
    } finally {
      setDeleteClassDialogOpen(false);
      setClassToDelete(null);
    }
  };

  const openAddStudentToClass = (classId) => {
    setSelectedClassForStudent(classId);
    setUserFormData({
      username: '',
      password: '',
      full_name: '',
      role: 'student',
      class_id: classId,
    });
    setUserDialogOpen(true);
  };

  // Open assign classes to teacher dialog
  const openAssignClassDialog = async (teacher) => {
    setSelectedTeacher(teacher);
    try {
      const response = await axios.get(`${API}/admin/teachers/${teacher.id}/assigned-classes`, {
        headers: getAuthHeaders(),
      });
      setSelectedClassIds(response.data.assigned_class_ids || []);
    } catch (error) {
      setSelectedClassIds([]);
    }
    setAssignClassDialogOpen(true);
  };

  // Assign classes to teacher
  const handleAssignClasses = async () => {
    if (!selectedTeacher) return;
    try {
      await axios.put(
        `${API}/admin/teachers/${selectedTeacher.id}/assign-classes`,
        { class_ids: selectedClassIds },
        { headers: getAuthHeaders() }
      );
      toast.success('Сыныптар сәтті тағайындалды');
      setAssignClassDialogOpen(false);
      setSelectedTeacher(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  const toggleClassSelection = (classId) => {
    setSelectedClassIds(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const toggleClassExpand = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  // Edit class name
  const startEditingClass = (cls) => {
    setEditingClassId(cls.id);
    setEditingClassName(cls.name);
  };

  const cancelEditingClass = () => {
    setEditingClassId(null);
    setEditingClassName('');
  };

  const saveClassName = async (classId) => {
    if (!editingClassName.trim()) {
      toast.error('Сынып атауы бос болмауы керек');
      return;
    }
    try {
      await axios.put(
        `${API}/admin/classes/${classId}`,
        { name: editingClassName.trim() },
        { headers: getAuthHeaders() }
      );
      toast.success('Сынып атауы өзгертілді');
      fetchClasses();
      setEditingClassId(null);
      setEditingClassName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  // Multi-select students
  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudentsInClass = (classId) => {
    const classStudents = studentsByClass[classId] || [];
    const classStudentIds = classStudents.map(s => s.id);
    const allSelected = classStudentIds.every(id => selectedStudents.includes(id));
    
    if (allSelected) {
      // Deselect all
      setSelectedStudents(prev => prev.filter(id => !classStudentIds.includes(id)));
    } else {
      // Select all
      setSelectedStudents(prev => [...new Set([...prev, ...classStudentIds])]);
    }
  };

  const clearSelectedStudents = () => {
    setSelectedStudents([]);
  };

  // Bulk delete students
  const handleBulkDelete = async () => {
    if (selectedStudents.length === 0) return;
    
    try {
      await axios.post(
        `${API}/admin/users/bulk-delete`,
        { user_ids: selectedStudents },
        { headers: getAuthHeaders() }
      );
      toast.success(`${selectedStudents.length} оқушы өшірілді`);
      fetchUsers();
      setSelectedStudents([]);
      setBulkActionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  // Bulk move students to another class
  const handleBulkMove = async () => {
    if (selectedStudents.length === 0 || !targetClassId) return;
    
    try {
      await axios.post(
        `${API}/admin/users/bulk-move`,
        { user_ids: selectedStudents, target_class_id: targetClassId },
        { headers: getAuthHeaders() }
      );
      toast.success(`${selectedStudents.length} оқушы ${getClassNameById(targetClassId)} сыныбына ауыстырылды`);
      fetchUsers();
      setSelectedStudents([]);
      setTargetClassId('');
      setBulkActionDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    }
  };

  const openBulkActionDialog = (type) => {
    setBulkActionType(type);
    setBulkActionDialogOpen(true);
  };

  const getRoleName = (role) => {
    if (role === 'admin') return 'Admin';
    if (role === 'teacher') return 'Мұғалім';
    return 'Оқушы';
  };

  const getClassNameById = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : '-';
  };

  // Filter users
  const filteredUsers = users.filter(u =>
    u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const teachers = filteredUsers.filter(u => u.role === 'teacher');
  const students = filteredUsers.filter(u => u.role === 'student');
  const admins = filteredUsers.filter(u => u.role === 'admin');

  // Group students by class
  const studentsByClass = {};
  classes.forEach(cls => {
    studentsByClass[cls.id] = students.filter(s => s.class_id === cls.id);
  });
  const studentsWithoutClass = students.filter(s => !s.class_id);

  const UserRow = ({ user, showClass = false, selectable = false }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-border/40 hover:shadow-md transition-all">
      <div className="flex items-center gap-3 flex-1">
        {selectable && (
          <Checkbox
            checked={selectedStudents.includes(user.id)}
            onCheckedChange={() => toggleStudentSelection(user.id)}
            data-testid={`select-student-${user.id}`}
          />
        )}
        <div className="flex-1">
          <h4 className="font-semibold text-sm">{user.full_name}</h4>
          <div className="flex items-center space-x-2 mt-1">
            <span className="text-xs text-muted">@{user.username}</span>
            {showClass && user.class_id && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                {getClassNameById(user.class_id)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedUser(user);
            setPasswordDialogOpen(true);
          }}
          title="Құпия сөзді өзгерту"
        >
          <Key className="h-4 w-4" />
        </Button>
        {user.id !== currentUser?.id && user.role !== 'admin' && (
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteUserDialog(user);
            }}
            title="Өшіру"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  const ClassCard = ({ cls }) => {
    const classStudents = studentsByClass[cls.id] || [];
    const isExpanded = expandedClasses[cls.id];
    const isEditing = editingClassId === cls.id;
    
    // Filter by search
    const filteredClassStudents = classStudents.filter(s =>
      s.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination for this class
    const classPage = currentPage[cls.id] || 1;
    const totalPages = Math.ceil(filteredClassStudents.length / studentsPerPage);
    const startIndex = (classPage - 1) * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    const paginatedStudents = filteredClassStudents.slice(startIndex, endIndex);

    const goToPage = (page) => {
      setCurrentPage(prev => ({ ...prev, [cls.id]: page }));
    };

    // Check if all students in this class are selected
    const classStudentIds = classStudents.map(s => s.id);
    const allSelected = classStudentIds.length > 0 && classStudentIds.every(id => selectedStudents.includes(id));
    const someSelected = classStudentIds.some(id => selectedStudents.includes(id));

    return (
      <Card className="paper-card">
        <Collapsible open={isExpanded} onOpenChange={() => toggleClassExpand(cls.id)}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-primary" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted" />
                  )}
                  <School className="h-5 w-5 text-primary" />
                  <div>
                    {isEditing ? (
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={editingClassName}
                          onChange={(e) => setEditingClassName(e.target.value)}
                          className="h-8 w-32"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveClassName(cls.id);
                            if (e.key === 'Escape') cancelEditingClass();
                          }}
                        />
                        <Button size="sm" variant="ghost" onClick={() => saveClassName(cls.id)}>
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEditingClass}>
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <CardTitle className="text-lg heading-font flex items-center gap-2">
                          {cls.name}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditingClass(cls);
                            }}
                            title="Атауын өзгерту"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                        </CardTitle>
                        <p className="text-sm text-muted">{classStudents.length} оқушы</p>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddStudentToClass(cls.id);
                    }}
                    title="Оқушы қосу"
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Оқушы қосу
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteClassDialog(cls);
                    }}
                    title="Сыныпты өшіру"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0 px-4 pb-4">
              {filteredClassStudents.length === 0 ? (
                <div className="text-center py-6 text-muted">
                  {searchTerm ? 'Іздеу бойынша оқушылар табылмады' : 'Бұл сыныпта оқушылар жоқ'}
                </div>
              ) : (
                <>
                  {/* Select All / Bulk Actions */}
                  <div className="flex items-center justify-between mb-3 py-2 border-b">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={() => selectAllStudentsInClass(cls.id)}
                        data-testid={`select-all-${cls.id}`}
                      />
                      <span className="text-sm text-muted">
                        {someSelected ? `${classStudentIds.filter(id => selectedStudents.includes(id)).length} таңдалды` : 'Барлығын таңдау'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mt-2">
                    {paginatedStudents.map(student => (
                      <UserRow key={student.id} user={student} selectable={true} />
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted">
                        {startIndex + 1}-{Math.min(endIndex, filteredClassStudents.length)} / {filteredClassStudents.length} оқушы
                      </p>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(classPage - 1)}
                          disabled={classPage === 1}
                        >
                          Артқа
                        </Button>
                        <span className="text-sm font-medium">
                          {classPage} / {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage(classPage + 1)}
                          disabled={classPage === totalPages}
                        >
                          Алға
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 w-96 bg-gray-200 rounded animate-pulse mt-2"></div>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="paper-card">
              <CardContent className="p-6">
                <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
                <div className="mt-4 space-y-3">
                  <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="user-management">
      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Пайдаланушыны өшіру</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{userToDelete?.full_name}&quot; атты пайдаланушыны өшіргіңіз келетініне сенімдісіз бе? Бұл әрекетті қайтару мүмкін емес.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Болдырмау</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUser} className="bg-red-600 hover:bg-red-700">
              Өшіру
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Class Confirmation Dialog */}
      <AlertDialog open={deleteClassDialogOpen} onOpenChange={setDeleteClassDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Сыныпты өшіру</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{classToDelete?.name}&quot; сыныбын өшіргіңіз келетініне сенімдісіз бе? Бұл әрекетті қайтару мүмкін емес.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Болдырмау</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteClass} className="bg-red-600 hover:bg-red-700">
              Өшіру
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkActionType === 'delete' ? 'Оқушыларды өшіру' : 'Оқушыларды ауыстыру'}
            </DialogTitle>
            <DialogDescription>
              {selectedStudents.length} оқушы таңдалды
            </DialogDescription>
          </DialogHeader>
          
          {bulkActionType === 'delete' ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  ⚠️ {selectedStudents.length} оқушыны өшіргіңіз келетініне сенімдісіз бе? Бұл әрекетті қайтару мүмкін емес.
                </p>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)}>
                  Болдырмау
                </Button>
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Өшіру
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Жаңа сынып таңдаңыз</Label>
                <Select value={targetClassId} onValueChange={setTargetClassId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Сынып таңдаңыз" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)}>
                  Болдырмау
                </Button>
                <Button onClick={handleBulkMove} disabled={!targetClassId}>
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Ауыстыру
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold heading-font tracking-tight">
            Пайдаланушылар басқаруы
          </h2>
          <p className="text-muted">Сыныптар мен пайдаланушыларды басқару</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Bulk Import Dialog */}
          <Dialog open={bulkImportDialogOpen} onOpenChange={(open) => {
            if (!open) resetBulkImportDialog();
            else setBulkImportDialogOpen(true);
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-import-btn">
                <Upload className="mr-2 h-4 w-4" />
                Топтап жүктеу
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Топтап жүктеу (Excel)</DialogTitle>
                <DialogDescription>
                  Excel файлынан оқушыларды, мұғалімдерді немесе сыныптарды топтап жүктеңіз
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Import Type Selection */}
                <div>
                  <Label>Жүктеу түрі</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <Button
                      type="button"
                      variant={bulkImportType === 'students' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setBulkImportType('students');
                        setSelectedFile(null);
                        setImportResults(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      data-testid="import-type-students"
                    >
                      <GraduationCap className="w-4 h-4 mr-1" />
                      Оқушылар
                    </Button>
                    <Button
                      type="button"
                      variant={bulkImportType === 'teachers' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setBulkImportType('teachers');
                        setSelectedFile(null);
                        setImportResults(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      data-testid="import-type-teachers"
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Мұғалімдер
                    </Button>
                    <Button
                      type="button"
                      variant={bulkImportType === 'classes' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setBulkImportType('classes');
                        setSelectedFile(null);
                        setImportResults(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      data-testid="import-type-classes"
                    >
                      <School className="w-4 h-4 mr-1" />
                      Сыныптар
                    </Button>
                  </div>
                </div>

                {/* Download Template */}
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => downloadTemplate(bulkImportType)}
                    className="w-full"
                    data-testid="download-template-btn"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {getImportTypeLabel(bulkImportType)} үлгі файлын жүктеу
                  </Button>
                </div>
                
                {/* File Input */}
                <div>
                  <Label>Excel файлын таңдаңыз</Label>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="mt-2"
                    data-testid="file-input"
                  />
                  {selectedFile && (
                    <p className="text-sm text-green-600 mt-2 flex items-center">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      {selectedFile.name}
                    </p>
                  )}
                </div>

                {/* Column Info */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-800 font-semibold mb-2">
                    {getImportTypeLabel(bulkImportType)} үшін бағандар:
                  </p>
                  <ul className="text-xs text-blue-700 space-y-1">
                    {getImportTypeColumns(bulkImportType).map((col, idx) => (
                      <li key={idx}>
                        • <span className="font-medium">{col.name}</span>
                        {col.required && <span className="text-red-500">*</span>}
                        <span className="text-blue-600 ml-1">({col.desc})</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Import Results */}
                {importResults && (
                  <div className={`p-3 rounded-lg ${importResults.created > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                    <p className="text-sm font-semibold mb-2">
                      {importResults.created > 0 ? '✅ Жүктеу нәтижесі:' : '⚠️ Жүктеу нәтижесі:'}
                    </p>
                    <ul className="text-xs space-y-1">
                      <li className="text-green-700">• Қосылды: {importResults.created}</li>
                      <li className="text-yellow-700">• Өткізілді: {importResults.skipped}</li>
                    </ul>
                    {importResults.details && importResults.details.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Толық ақпарат:</p>
                        <div className="space-y-1">
                          {importResults.details.slice(0, 10).map((item, idx) => (
                            <div key={idx} className={`text-xs px-2 py-1 rounded ${item.status === 'қосылды' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {item.name} - {item.status}
                              {item.username && <span className="ml-1">(@{item.username})</span>}
                              {item.reason && <span className="ml-1">({item.reason})</span>}
                            </div>
                          ))}
                          {importResults.details.length > 10 && (
                            <p className="text-xs text-gray-500">...және тағы {importResults.details.length - 10}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  onClick={handleBulkImportSubmit}
                  disabled={!selectedFile || importing}
                  className="w-full"
                  data-testid="bulk-import-submit"
                >
                  {importing ? (
                    <>
                      <Upload className="mr-2 h-4 w-4 animate-spin" />
                      Жүктелуде...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      {getImportTypeLabel(bulkImportType)} жүктеу
                    </>
                  )}
                </Button>

                {/* Close Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetBulkImportDialog}
                  className="w-full"
                >
                  Жабу
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Class Dialog */}
          <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <FolderPlus className="mr-2 h-4 w-4" />
                Жаңа сынып
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Жаңа сынып қосу</DialogTitle>
                <DialogDescription>
                  Жаңа сынып атауы мен деңгейін енгізіңіз
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleClassSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="class_name">Сынып атауы</Label>
                  <Input
                    id="class_name"
                    value={classFormData.name}
                    onChange={(e) => setClassFormData({ ...classFormData, name: e.target.value })}
                    placeholder="Мысалы: 10-А"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="class_grade">Сынып деңгейі</Label>
                  <Input
                    id="class_grade"
                    value={classFormData.grade}
                    onChange={(e) => setClassFormData({ ...classFormData, grade: e.target.value })}
                    placeholder="Мысалы: 10"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setClassDialogOpen(false)}>
                    Болдырмау
                  </Button>
                  <Button type="submit">
                    Сақтау
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add User Dialog */}
          <Dialog open={userDialogOpen} onOpenChange={(open) => {
            setUserDialogOpen(open);
            if (!open) {
              setSelectedClassForStudent(null);
              setUserFormData({
                username: '',
                password: '',
                full_name: '',
                role: 'student',
                class_id: '',
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Жаңа пайдаланушы
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selectedClassForStudent 
                    ? `${getClassNameById(selectedClassForStudent)} сыныбына оқушы қосу`
                    : 'Жаңа пайдаланушы қосу'
                  }
                </DialogTitle>
                <DialogDescription>
                  Пайдаланушы мәліметтерін толтырыңыз
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUserSubmit} className="space-y-4">
                {!selectedClassForStudent && (
                  <div>
                    <Label htmlFor="role">Рөлі</Label>
                    <Select
                      value={userFormData.role}
                      onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="teacher">Мұғалім</SelectItem>
                        <SelectItem value="student">Оқушы</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="full_name">Толық аты</Label>
                  <Input
                    id="full_name"
                    value={userFormData.full_name}
                    onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="username">Пайдаланушы аты</Label>
                  <Input
                    id="username"
                    value={userFormData.username}
                    onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password">Құпия сөз</Label>
                  <Input
                    id="password"
                    type="password"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    required
                  />
                </div>

                {(userFormData.role === 'student' && !selectedClassForStudent) && (
                  <div>
                    <Label htmlFor="class_id">Сынып</Label>
                    <Select
                      value={userFormData.class_id}
                      onValueChange={(value) => setUserFormData({ ...userFormData, class_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Сынып таңдаңыз" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => {
                    setUserDialogOpen(false);
                    setSelectedClassForStudent(null);
                  }}>
                    Болдырмау
                  </Button>
                  <Button type="submit">
                    Сақтау
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Құпия сөзді өзгерту</DialogTitle>
            <DialogDescription>
              Пайдаланушының жаңа құпия сөзін енгізіңіз
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <p className="text-sm text-muted mb-4">
                  {selectedUser.full_name} үшін жаңа құпия сөз
                </p>
                <Label htmlFor="new_password">Жаңа құпия сөз</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Кемінде 4 таңба"
                  required
                  minLength={4}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPasswordDialogOpen(false);
                    setNewPassword('');
                    setSelectedUser(null);
                  }}
                >
                  Болдырмау
                </Button>
                <Button type="submit">
                  Өзгерту
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Classes to Teacher Dialog */}
      <Dialog open={assignClassDialogOpen} onOpenChange={setAssignClassDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Сынып жетекшісі етіп тағайындау</DialogTitle>
            <DialogDescription>
              Мұғалімге сынып жетекшілігін тағайындаңыз
            </DialogDescription>
          </DialogHeader>
          {selectedTeacher && (
            <div className="space-y-4">
              <p className="text-sm text-muted">
                <span className="font-semibold">{selectedTeacher.full_name}</span> мұғаліміне сынып жетекшілігін тағайындаңыз
              </p>
              
              <div className="border rounded-lg p-3 max-h-[300px] overflow-y-auto space-y-2">
                {classes.map((cls) => (
                  <label
                    key={cls.id}
                    className="flex items-center space-x-3 p-2 hover:bg-accent/50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClassIds.includes(cls.id)}
                      onChange={() => toggleClassSelection(cls.id)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="font-medium">{cls.name}</span>
                  </label>
                ))}
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAssignClassDialogOpen(false);
                    setSelectedTeacher(null);
                    setSelectedClassIds([]);
                  }}
                >
                  Болдырмау
                </Button>
                <Button onClick={handleAssignClasses}>
                  Сақтау
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-4 w-4" />
        <Input
          placeholder="Пайдаланушы іздеу..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <School className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold heading-font">{classes.length}</div>
            <p className="text-xs text-muted">Сыныптар</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold heading-font">{teachers.length}</div>
            <p className="text-xs text-muted">Мұғалімдер</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <GraduationCap className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold heading-font">{students.length}</div>
            <p className="text-xs text-muted">Оқушылар</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <Key className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold heading-font">{users.length}</div>
            <p className="text-xs text-muted">Барлығы</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="classes" className="space-y-4">
        <TabsList>
          <TabsTrigger value="classes">Сыныптар ({classes.length})</TabsTrigger>
          <TabsTrigger value="teachers">Мұғалімдер ({teachers.length})</TabsTrigger>
          <TabsTrigger value="admins">Admin ({admins.length})</TabsTrigger>
        </TabsList>

        {/* Classes Tab - Students grouped by class */}
        <TabsContent value="classes" className="space-y-4">
          {/* Bulk Action Bar - Shows when students are selected */}
          {selectedStudents.length > 0 && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckSquare className="h-5 w-5 text-teal-600" />
                <span className="font-medium text-teal-800">
                  {selectedStudents.length} оқушы таңдалды
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openBulkActionDialog('move')}
                  className="border-teal-300 text-teal-700 hover:bg-teal-100"
                >
                  <ArrowRightLeft className="h-4 w-4 mr-1" />
                  Сынып ауыстыру
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => openBulkActionDialog('delete')}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Өшіру
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelectedStudents}
                >
                  <X className="h-4 w-4 mr-1" />
                  Болдырмау
                </Button>
              </div>
            </div>
          )}

          {classes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted">
                <School className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Сыныптар табылмады</p>
                <p className="text-sm mt-2">Жаңа сынып қосу үшін жоғарыдағы батырманы басыңыз</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {classes.map(cls => (
                <ClassCard key={cls.id} cls={cls} />
              ))}
              
              {/* Students without class */}
              {studentsWithoutClass.length > 0 && (
                <Card className="paper-card border-dashed">
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <GraduationCap className="h-5 w-5 text-muted" />
                      <div>
                        <CardTitle className="text-lg heading-font text-muted">Сыныпсыз оқушылар</CardTitle>
                        <p className="text-sm text-muted">{studentsWithoutClass.length} оқушы</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4">
                    <div className="space-y-2">
                      {studentsWithoutClass.map(student => (
                        <UserRow key={student.id} user={student} />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Teachers Tab */}
        <TabsContent value="teachers" className="space-y-3">
          {teachers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Мұғалімдер табылмады</p>
              </CardContent>
            </Card>
          ) : (
            teachers.map(teacher => (
              <div key={teacher.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-border/40 hover:shadow-md transition-all">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm">{teacher.full_name}</h4>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-muted">@{teacher.username}</span>
                    {teacher.assigned_class_ids?.length > 0 && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        Сынып жетекшісі: {teacher.assigned_class_ids.map(id => getClassNameById(id)).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openAssignClassDialog(teacher)}
                    title="Сынып тағайындау"
                    className="text-xs"
                  >
                    <School className="h-4 w-4 mr-1" />
                    Сынып
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(teacher);
                      setPasswordDialogOpen(true);
                    }}
                    title="Құпия сөзді өзгерту"
                  >
                    <Key className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => openDeleteUserDialog(teacher)}
                    title="Өшіру"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Admins Tab */}
        <TabsContent value="admins" className="space-y-3">
          {admins.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted">
                <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Admin табылмады</p>
              </CardContent>
            </Card>
          ) : (
            admins.map(user => <UserRow key={user.id} user={user} showClass />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
